/**
 * Fishing rules (Bedrock 1.21 / Java 26.x numbers): bite timer, loot tables,
 * XP and durability costs. DOM-free so the node smoke tests can run it; the
 * bobber entity (entities/vehicles/bobber.ts) drives `FishTimer` each tick.
 *
 *   wait   100..600 ticks (5-30 s); Lure -100 ticks / level, rain x0.8, no sky x1.25 (min 20)
 *   trail  fish swims in, 20..80 ticks of bubble / wake particles
 *   bite   bobber dips; reel within 20..40 ticks or the fish escapes and the timer restarts
 *   loot   fish 85 (quality -1) / junk 10 (-2) / treasure 5 (+2), weight = base + floor(quality * luck)
 */

import { I } from './itemIds';
import { JavaRandom, selectEnchantments } from './enchant/table';
import { E } from './enchant/registry';
import { itemDef } from './itemdb';
import * as B from '../blocks';
import { stackOf, type Stack } from './types';

export type Rnd = () => number;
const ri = (rnd: Rnd, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

export interface FishCtx { lure: number; rain: boolean; sky: boolean }

/** Ticks until a fish notices the bait. */
export function rollWait(rnd: Rnd, c: FishCtx): number {
  let t = ri(rnd, 100, 600);
  if (c.rain) t *= 0.8;
  if (!c.sky) t *= 1.25;
  t -= 100 * c.lure;
  return Math.max(20, Math.round(t));
}

export type FishState = 'wait' | 'trail' | 'bite';
export type FishEvent = 'none' | 'trail' | 'bite' | 'escaped';

/** The bait timer of one bobber floating in water. */
export class FishTimer {
  state: FishState = 'wait';
  ticks: number;
  constructor(private readonly rnd: Rnd, public ctx: FishCtx) { this.ticks = rollWait(rnd, ctx); }

  tick(): FishEvent {
    if (--this.ticks > 0) return 'none';
    if (this.state === 'wait') {
      this.state = 'trail'; this.ticks = ri(this.rnd, 20, 80); return 'trail';
    }
    if (this.state === 'trail') {
      this.state = 'bite'; this.ticks = ri(this.rnd, 20, 40); return 'bite';
    }
    this.state = 'wait'; this.ticks = rollWait(this.rnd, this.ctx);
    return 'escaped';
  }
}

export type CatchKind = 'fish' | 'junk' | 'treasure';

export function kindWeights(luck: number): { fish: number; junk: number; treasure: number } {
  return {
    fish: Math.max(0, 85 - luck),
    junk: Math.max(0, 10 - 2 * luck),
    treasure: Math.max(0, 5 + 2 * luck),
  };
}

export function rollKind(rnd: Rnd, luck: number): CatchKind {
  const w = kindWeights(luck);
  const tot = w.fish + w.junk + w.treasure;
  let r = rnd() * tot;
  if ((r -= w.fish) < 0) return 'fish';
  if ((r -= w.junk) < 0) return 'junk';
  return 'treasure';
}

function pick<T>(rnd: Rnd, list: readonly (readonly [number, T])[]): T {
  let tot = 0;
  for (const [w] of list) tot += w;
  let r = rnd() * tot;
  for (const [w, v] of list) if ((r -= w) < 0) return v;
  return list[list.length - 1][1];
}

const FISH: readonly (readonly [number, number])[] = [[60, I.COD], [25, I.SALMON], [2, I.TROPICAL_FISH], [13, I.PUFFERFISH]];

/** Damaged item: Java rolls 0..90 % of the durability as already used. */
function worn(id: number, rnd: Rnd): Stack {
  const s = stackOf(id);
  s.dmg = Math.floor(itemDef(id).maxDamage! * rnd() * 0.9);
  return s;
}

/** One catch, given the Luck of the Sea level. */
export function rollCatch(rnd: Rnd, luck: number): Stack {
  const kind = rollKind(rnd, luck);
  if (kind === 'fish') return stackOf(pick(rnd, FISH));
  if (kind === 'junk') {
    const junk: (readonly [number, () => Stack])[] = [
      [17, () => stackOf(B.LILY_PAD_ID)], [10, () => worn(I.LEATHER_BOOTS, rnd)], [10, () => stackOf(I.LEATHER)],
      [10, () => stackOf(I.BONE)], [10, () => stackOf(I.POTION)], [5, () => stackOf(I.STRING)],
      [2, () => worn(I.FISHING_ROD, rnd)], [10, () => stackOf(I.BOWL)], [5, () => stackOf(I.STICK)],
      [1, () => stackOf(I.INK_SAC, 10)], [10, () => stackOf(I.ROTTEN_FLESH)],
    ];
    return pick(rnd, junk)();
  }
  const enchant = (base: number, as: number): Stack => {
    const s = as === I.ENCHANTED_BOOK ? stackOf(base) : worn(base, rnd);
    const r = new JavaRandom(Math.floor(rnd() * 2 ** 31));
    s.ench = selectEnchantments(r, as === I.ENCHANTED_BOOK ? I.BOOK : base, 30, true).map((e) => ({ id: e.id, lvl: e.lvl }));
    if (!s.ench.length) delete s.ench;
    return s;
  };
  const t: (readonly [number, () => Stack])[] = [
    [1, () => enchant(I.BOW, I.BOW)], [1, () => enchant(I.ENCHANTED_BOOK, I.ENCHANTED_BOOK)],
    [1, () => enchant(I.FISHING_ROD, I.FISHING_ROD)], [1, () => stackOf(I.NAME_TAG)],
    [1, () => stackOf(I.NAUTILUS_SHELL)], [1, () => stackOf(I.SADDLE)],
  ];
  return pick(rnd, t)();
}

/** XP orb value for a catch (vanilla 1..6). */
export const rollXp = (rnd: Rnd): number => ri(rnd, 1, 6);

export type ReelOutcome = 'catch' | 'block' | 'mob' | 'item' | 'nothing';
/** Rod durability lost on reeling in: 1 catch / 3 hooked block / 5 pulled mob; items 2 (Java); an empty reel costs 0. */
export function rodDamage(o: ReelOutcome): number {
  switch (o) { case 'catch': return 1; case 'block': return 3; case 'mob': return 5; case 'item': return 3; default: return 0; }
}

export { E as FISHING_ENCH };
